/**
 * Catalog sources.
 *
 * A HangarMax scene references catalog entries by UUID only, so dimensions and
 * names must come from somewhere else:
 *
 *  - `item_cache` — embedded in admin export bundles. Dimensions, model hash and
 *    procedural flags, but NO names, so aircraft can only be matched by size.
 *  - Catalog manifest — `cdn.hangarmax.com/data/catalog-manifest.json` (fetched
 *    through the `hangarmax-share-import` edge function, since the CDN sends no
 *    CORS headers). Carries display names, which unlocks name-based matching.
 *
 * `mergedCatalogSource` prefers manifest entries and falls back to item_cache,
 * so a bundle import gets the best of both when the manifest is reachable and
 * still works offline when it isn't.
 */

import type {
  CatalogSource,
  HmCatalogEntry,
  HmItemCache,
  HmManifestItem,
} from './types'

function entryFromManifest(item: HmManifestItem): HmCatalogEntry {
  return {
    catalogId: item.id,
    name: item.n?.trim() || null,
    manufacturer: item.mf?.trim() || null,
    category: item.c ?? null,
    widthM: item.w ?? null,
    lengthM: item.l ?? null,
    heightM: item.h ?? null,
    isProcedural: item.mt === 'p',
    proceduralShape: item.ps ?? null,
    proceduralColor: item.pc ?? null,
    modelHash: item.m ?? null,
  }
}

export function manifestCatalogSource(items: HmManifestItem[]): CatalogSource {
  const map = new Map<string, HmCatalogEntry>()
  for (const item of items) {
    if (item?.id) map.set(item.id, entryFromManifest(item))
  }

  let hasNames = false
  for (const entry of map.values()) {
    if (entry.name) {
      hasNames = true
      break
    }
  }

  return {
    get: (id) => (id ? map.get(id) : undefined),
    hasNames,
  }
}

export function itemCacheCatalogSource(cache: HmItemCache | null | undefined): CatalogSource {
  const map = new Map<string, HmCatalogEntry>()

  for (const [catalogId, raw] of Object.entries(cache ?? {})) {
    map.set(catalogId, {
      catalogId,
      name: null,
      manufacturer: null,
      category: null,
      widthM: raw?.w ?? null,
      lengthM: raw?.l ?? null,
      heightM: raw?.h ?? null,
      isProcedural: raw?.mt === 'p',
      proceduralShape: raw?.ps ?? null,
      proceduralColor: raw?.pc ?? null,
      modelHash: raw?.m ?? null,
    })
  }

  return {
    get: (id) => (id ? map.get(id) : undefined),
    hasNames: false,
  }
}

/** Field-level merge: manifest wins where it has a value, item_cache fills gaps. */
export function mergedCatalogSource(
  primary: CatalogSource,
  fallback: CatalogSource
): CatalogSource {
  return {
    get(id) {
      const a = primary.get(id)
      const b = fallback.get(id)
      if (!a) return b
      if (!b) return a
      return {
        catalogId: a.catalogId,
        name: a.name ?? b.name,
        manufacturer: a.manufacturer ?? b.manufacturer,
        category: a.category ?? b.category,
        widthM: a.widthM ?? b.widthM,
        lengthM: a.lengthM ?? b.lengthM,
        heightM: a.heightM ?? b.heightM,
        isProcedural: a.isProcedural || b.isProcedural,
        proceduralShape: a.proceduralShape ?? b.proceduralShape,
        proceduralColor: a.proceduralColor ?? b.proceduralColor,
        modelHash: a.modelHash ?? b.modelHash,
      }
    },
    hasNames: primary.hasNames || fallback.hasNames,
  }
}

export const EMPTY_CATALOG_SOURCE: CatalogSource = {
  get: () => undefined,
  hasNames: false,
}
